/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    string, object, shape, oneOfType, bool,
} from 'prop-types';

/**
 * Responsive Image Component
 * @param {string} path
 * @param {string} alt
 * @param {object} params
 * @param {object} breakpoints
 * @param {string} className
 * @param {boolean} isExternalSource
 */
const ResponsiveImage = ({
    path,
    alt,
    role,
    ariaLabel,
    params = {},
    breakpoints = {},
    className,
    style = {},
    isExternalSource,
    fullWidth = false,
}) => {
    const { desktop: desktopP = 'quality=90', tablet: tabletP = 'quality=85', mobile: mobileP = 'quality=80' } = params;
    const { desktop: desktopB = '1024px', tablet: tabletB = '640px', mobile: mobileB = '200px' } = breakpoints;
    const defaultParams = 'auto=webp&optimize={medium}'; // if no params & needs to be ?

    /**
     * If things are coming from someone else, dont spam with
     * interally defined parameters in query.
     */
    if (isExternalSource) {
        return (
            <picture>
                <source type="image/jpeg" media={`(min-width: ${desktopB})`} srcSet={`${path}`} />
                <source type="image/jpeg" media={`(min-width: ${tabletB})`} srcSet={`${path}`} />
                <source type="image/jpeg" media={`(min-width: ${mobileB})`} srcSet={`${path}`} />
                {/* img fallback if browser doesn't support picture */}
                {role
                    ? (
                        <img style={style} className={className} src={`${path}`} alt={alt} role={role} aria-label={ariaLabel} />
                    ) : (
                        <img style={style} className={className} src={`${path}`} alt={alt} />
                    )}
            </picture>
        );
    }

    return (
        <picture style={{ width: (fullWidth ? '100%' : 'auto') }}>
            <source type="image/jpeg" media={`(min-width: ${desktopB})`} srcSet={`${path}${desktopP ? `?${desktopP}` : '?'}${desktopP ? `&${defaultParams}` : defaultParams}`} />
            <source type="image/jpeg" media={`(min-width: ${tabletB})`} srcSet={`${path}${tabletP ? `?${tabletP}` : '?'}${tabletP ? `&${defaultParams}` : defaultParams}`} />
            <source type="image/jpeg" media={`(min-width: ${mobileB})`} srcSet={`${path}${mobileP ? `?${mobileP}` : '?'}${mobileP ? `&${defaultParams}` : defaultParams}`} />
            {/* img fallback if browser doesn't support picture */}
            {role
                ? (
                    <img style={style} className={className} src={`${path}?${defaultParams}`} alt={alt} role={role} aria-label={ariaLabel} />
                ) : (
                    <img style={style} className={className} src={`${path}?${defaultParams}`} alt={alt} />
                )}
        </picture>
    );
};

// TODO: Default params provided by default props

ResponsiveImage.propTypes = {
    path: string.isRequired,
    alt: string,
    role: string,
    ariaLabel: string,
    params: shape({
        desktop: string,
        tablet: string,
        mobile: string,
    }),
    breakpoints: shape({
        desktop: string,
        tablet: string,
        mobile: string,
    }),
    className: oneOfType([
        string,
        object,
    ]).isRequired,
    style: object,
    isExternalSource: bool,
    fullWidth: bool,
};

ResponsiveImage.defaultProps = {
    alt: '',
    role: '',
    ariaLabel: '',
    params: {},
    breakpoints: {},
    style: {},
    isExternalSource: false,
    fullWidth: false,
};

export default ResponsiveImage;
